export const getTimestampValue = (row: Record<string, unknown>) => {
  const keys = Object.keys(row);

  let latest = Number.NEGATIVE_INFINITY;

  keys.forEach((key) => {
    const lowerKey = key.toLowerCase();
    const isTimestampKey =
      lowerKey.endsWith('_at') ||
      lowerKey.includes('timestamp') ||
      lowerKey === 'time';

    if (!isTimestampKey) {
      return;
    }

    const rawValue = row[key];

    if (typeof rawValue !== 'string' && typeof rawValue !== 'number') {
      return;
    }

    const parsed = new Date(rawValue).getTime();

    if (!Number.isNaN(parsed) && parsed > latest) {
      latest = parsed;
    }
  });

  return latest;
};

export const normalizeWithoutTimestamps = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeWithoutTimestamps(item));
  }

  if (!value || typeof value !== 'object') {
    return value;
  }

  const objectValue = value as Record<string, unknown>;
  const sortedKeys = Object.keys(objectValue).sort();

  return sortedKeys.reduce<Record<string, unknown>>((acc, key) => {
    const lowerKey = key.toLowerCase();
    const isTimestampKey =
      lowerKey.endsWith('_at') ||
      lowerKey.includes('timestamp') ||
      lowerKey === 'time';
    const isIdKey = lowerKey === 'id';

    if (isTimestampKey || isIdKey) {
      return acc;
    }

    acc[key] = normalizeWithoutTimestamps(objectValue[key]);

    return acc;
  }, {});
};

export const processTableData = (tableData: Record<string, unknown>[]) => {
  const groupedIndexes = new Map<string, number[]>();

  tableData.forEach((row, index) => {
    const duplicateKey = JSON.stringify(normalizeWithoutTimestamps(row));
    const existingIndexes = groupedIndexes.get(duplicateKey);

    if (existingIndexes) {
      existingIndexes.push(index);
      return;
    }

    groupedIndexes.set(duplicateKey, [index]);
  });

  const mostRecentIndexes = new Set<number>();

  groupedIndexes.forEach((indexes) => {
    const mostRecentIndex = indexes.reduce((latestIndex, currentIndex) => {
      const latestTimestamp = getTimestampValue(tableData[latestIndex]);
      const currentTimestamp = getTimestampValue(tableData[currentIndex]);

      return currentTimestamp > latestTimestamp ? currentIndex : latestIndex;
    }, indexes[0]);

    mostRecentIndexes.add(mostRecentIndex);
  });

  return tableData.map((row, index) => {
    if (!mostRecentIndexes.has(index)) {
      return row;
    }

    return {
      ...row,
      mostRecent: true
    };
  });
};
